import * as React from 'react';
import { MosaicDirection, MosaicKey } from 'react-mosaic-component/lib/types';

import {
  MosaicCollapseOptionsById,
  MosaicCollapsedById,
} from 'src/components/mosaic/types';
import {
  EMPTY_TILE_ID,
  MosaicSplitOptions,
  MosaicSplitType,
  NestedMosaicParent,
  NestedMosaicParentInitial,
} from 'src/constants/mosaic';

export type MosaicComponentProps<T extends MosaicKey> = {
  id: T;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
};

export const getComponentMap = <T extends MosaicKey>(
  children: React.ReactNode,
  collapsedById: MosaicCollapsedById<T>,
  onToggleCollapsed: (collapsed: MosaicCollapsedById<T>) => void
) => {
  const childrenArr = React.Children.toArray(children) as JSX.Element[];
  return childrenArr.reduce<{ [key: string]: JSX.Element }>(
    (compMap, child) => {
      if (!child.props.id) {
        return compMap;
      }
      const id = child.props.id as T;
      const collapsed = collapsedById[id];
      return {
        ...compMap,
        [id]: React.cloneElement(child, {
          collapsed,
          onToggleCollapsed: () =>
            onToggleCollapsed({ ...collapsedById, [id]: !collapsed }),
        }),
      };
    },
    {}
  );
};

export const splitValueToPercent = (
  containerSize: { height: number; width: number },
  direction: MosaicDirection,
  splitType: MosaicSplitType,
  splitValue: number
) => {
  if (splitType === MosaicSplitType.PERCENT) {
    return splitValue;
  }

  const size =
    direction === 'column' ? containerSize.height : containerSize.width;
  return (splitValue / size) * 100;
};

export const determineNextContainerSize = (
  containerSize: { height: number; width: number },
  direction: MosaicDirection,
  getNewValue: (value: number) => number
) => {
  const nextContainerSize = { ...containerSize };
  if (direction === 'column') {
    nextContainerSize.height = getNewValue(containerSize.height);
  } else {
    nextContainerSize.width = getNewValue(containerSize.width);
  }
  return nextContainerSize;
};

export const determineInitialMosaicValue = <T extends MosaicKey>(
  containerSize: { height: number; width: number },
  initialValue: NestedMosaicParentInitial<T>,
  splitOptions: MosaicSplitOptions
): NestedMosaicParent<T> => {
  const {
    first,
    second,
    firstMinSplitType,
    firstMinSplitValue,
    secondMinSplitType,
    secondMinSplitValue,
    initialSplitPercentage,
  } = splitOptions;
  const minSplit = splitValueToPercent(
    containerSize,
    initialValue.direction,
    firstMinSplitType,
    firstMinSplitValue
  );
  const maxSplit =
    100 -
    splitValueToPercent(
      containerSize,
      initialValue.direction,
      secondMinSplitType,
      secondMinSplitValue
    );
  const newValue = {
    ...initialValue,
    splitPercentage: initialValue.splitPercentage ?? initialSplitPercentage,
  } as NestedMosaicParent<T>;
  if (newValue.splitPercentage < minSplit && newValue.first !== EMPTY_TILE_ID) {
    newValue.splitPercentage = minSplit;
  } else if (
    newValue.splitPercentage > maxSplit &&
    newValue.second !== EMPTY_TILE_ID
  ) {
    newValue.splitPercentage = maxSplit;
  }

  if (typeof newValue.first === 'object') {
    newValue.first = determineInitialMosaicValue(
      determineNextContainerSize(
        containerSize,
        newValue.direction,
        (val: number) => (val * newValue.splitPercentage) / 100
      ),
      newValue.first as NestedMosaicParentInitial<T>,
      first || splitOptions
    );
  }

  if (typeof newValue.second === 'object') {
    newValue.second = determineInitialMosaicValue(
      determineNextContainerSize(
        containerSize,
        newValue.direction,
        (val: number) => (val * (100 - newValue.splitPercentage)) / 100
      ),
      newValue.second as NestedMosaicParentInitial<T>,
      second || splitOptions
    );
  }

  return newValue;
};

export const determineUpdatedMosaicValue = <T extends MosaicKey>(
  containerSize: { height: number; width: number } | null,
  initialValue: NestedMosaicParent<T>,
  collapseOptions: MosaicCollapseOptionsById<T>,
  collapsedById: MosaicCollapsedById<T>,
  splitOptions: MosaicSplitOptions,
  setIdCollapsed: (id: T, value: boolean) => void
) => {
  const { splitPercentage: changeSplitPercentage } = initialValue;
  if (!changeSplitPercentage || !containerSize) {
    return initialValue;
  }

  const {
    first,
    second,
    firstMinSplitType,
    firstMinSplitValue,
    secondMinSplitType,
    secondMinSplitValue,
  } = splitOptions;

  const minSplitPercentage = splitValueToPercent(
    containerSize,
    initialValue.direction,
    firstMinSplitType,
    firstMinSplitValue
  );
  const maxSplitPercentage =
    100 -
    splitValueToPercent(
      containerSize,
      initialValue.direction,
      secondMinSplitType,
      secondMinSplitValue
    );

  const newValue = { ...initialValue };
  const firstId =
    typeof newValue.first === 'object' ? ('' as T) : newValue.first;
  const secondId =
    typeof newValue.second === 'object' ? ('' as T) : newValue.second;

  let isFirstCollapsed: boolean = collapsedById[firstId] ?? false;
  let isSecondCollapsed: boolean = collapsedById[secondId] ?? false;

  if (changeSplitPercentage < minSplitPercentage && firstId !== EMPTY_TILE_ID) {
    isFirstCollapsed = true;
    isSecondCollapsed = false;
    newValue.splitPercentage = minSplitPercentage;
  } else if (
    changeSplitPercentage > maxSplitPercentage &&
    secondId !== EMPTY_TILE_ID
  ) {
    isFirstCollapsed = false;
    isSecondCollapsed = true;
    newValue.splitPercentage = maxSplitPercentage;
  } else {
    isFirstCollapsed = false;
    isSecondCollapsed = false;
  }

  if (firstId && (collapseOptions[firstId]?.collapsible ?? true)) {
    setIdCollapsed(firstId, isFirstCollapsed);
  }
  if (secondId && (collapseOptions[secondId]?.collapsible ?? true)) {
    setIdCollapsed(secondId, isSecondCollapsed);
  }

  if (typeof newValue.first === 'object') {
    newValue.first = determineUpdatedMosaicValue(
      determineNextContainerSize(
        containerSize,
        newValue.direction,
        (val: number) => (val * newValue.splitPercentage) / 100
      ),
      newValue.first as NestedMosaicParent<T>,
      collapseOptions,
      collapsedById,
      first || splitOptions,
      setIdCollapsed
    );
  }

  if (typeof newValue.second === 'object') {
    newValue.second = determineUpdatedMosaicValue(
      determineNextContainerSize(
        containerSize,
        newValue.direction,
        (val: number) => (val * (100 - newValue.splitPercentage)) / 100
      ),
      newValue.second as NestedMosaicParent<T>,
      collapseOptions,
      collapsedById,
      second || splitOptions,
      setIdCollapsed
    );
  }

  return newValue;
};

export const determineOpenedMosaicValue = <T extends MosaicKey>(
  initialValue: NestedMosaicParent<T>,
  splitOptions: MosaicSplitOptions,
  collapsedById: MosaicCollapsedById<T>,
  prevCollapsedById: MosaicCollapsedById<T>
) => {
  const { splitPercentage: changeSplitPercentage } = initialValue;
  if (!changeSplitPercentage) {
    return initialValue;
  }

  const newValue = { ...initialValue };

  if (typeof newValue.second === 'object') {
    newValue.second = determineOpenedMosaicValue(
      newValue.second as NestedMosaicParent<T>,
      splitOptions.second || splitOptions,
      collapsedById,
      prevCollapsedById
    );
  } else if (
    collapsedById[newValue.second] !== prevCollapsedById[newValue.second] &&
    !collapsedById[newValue.second]
  ) {
    newValue.splitPercentage = splitOptions.initialSplitPercentage;
  }

  if (typeof newValue.first === 'object') {
    newValue.first = determineOpenedMosaicValue(
      newValue.first as NestedMosaicParent<T>,
      splitOptions.first || splitOptions,
      collapsedById,
      prevCollapsedById
    );
  } else if (
    collapsedById[newValue.first] !== prevCollapsedById[newValue.first] &&
    !collapsedById[newValue.first]
  ) {
    newValue.splitPercentage = splitOptions.initialSplitPercentage;
  }

  return newValue;
};

export const determineCollapsedMosaicValue = <T extends MosaicKey>(
  containerSize: { height: number; width: number },
  initialValue: NestedMosaicParent<T>,
  collapseOptions: MosaicCollapseOptionsById<T>,
  collapsedById: MosaicCollapsedById<T>
) => {
  const { splitPercentage: changeSplitPercentage } = initialValue;
  if (!changeSplitPercentage) {
    return initialValue;
  }

  const newValue = { ...initialValue };
  const firstId =
    typeof newValue.first === 'object' ? ('' as T) : newValue.first;
  const secondId =
    typeof newValue.second === 'object' ? ('' as T) : newValue.second;

  const firstCollapsedOptions = collapseOptions[firstId];
  const secondCollapsedOptions = collapseOptions[secondId];

  const size =
    initialValue.direction === 'column'
      ? containerSize.height
      : containerSize.width;

  if (collapsedById[firstId] && firstCollapsedOptions) {
    newValue.splitPercentage =
      (firstCollapsedOptions.collapsedSize / size) * 100;
  } else if (collapsedById[secondId] && secondCollapsedOptions) {
    newValue.splitPercentage =
      ((size - secondCollapsedOptions.collapsedSize) / size) * 100;
  }

  if (typeof newValue.first === 'object') {
    newValue.first = determineCollapsedMosaicValue(
      determineNextContainerSize(
        containerSize,
        newValue.direction,
        (val: number) => (val * newValue.splitPercentage) / 100
      ),
      newValue.first as NestedMosaicParent<T>,
      collapseOptions,
      collapsedById
    );
  }

  if (typeof newValue.second === 'object') {
    newValue.second = determineCollapsedMosaicValue(
      determineNextContainerSize(
        containerSize,
        newValue.direction,
        (val: number) => (val * (100 - newValue.splitPercentage)) / 100
      ),
      newValue.second as NestedMosaicParent<T>,
      collapseOptions,
      collapsedById
    );
  }

  return newValue;
};
